import React, { useEffect, useMemo, useState } from "react";
import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BackLink } from "@/components/BackLink";
import { CalendarSubscribe, CalendarSubscribeDialog } from "@/components/CalendarSubscribe";
import { MapPin, Clock, CalendarDays, LocateFixed, Globe2, Laptop, CalendarPlus } from "lucide-react";

// Phase 10.1 — calendar relevance scope ("Near me" vs "UK-wide").
// Preference persists in localStorage (per-device view pref; no profile
// schema change needed): calendar_scope = "near"|"uk",
// calendar_radius_miles = 5..50 (default 15).
const milesBetween = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3958.8; // earth radius, miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Remote events are relevant anywhere, so they stay visible under Near me.
// Some seeds mark remoteness only via location text ("Online"), hence the
// fallback heuristic for coordinate-less events.
const isRemoteEvent = (o) =>
  o.remote === true ||
  (o.lat == null && o.lng == null && /online|remote/i.test(o.location || ""));

export default function CalendarPage() {
  const { user } = useAuth();
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const hasGeo = user?.lat != null && user?.lng != null;
  const [opps, setOpps] = useState([]);
  const [selected, setSelected] = useState(new Date());
  // null = no explicit choice yet -> smart default: "near" when the profile
  // has a location (relevance-first, tester follow-up 2026-07), else "uk".
  // An explicit choice (chip click) is stored and always wins afterwards.
  const [scope, setScopeState] = useState(() => {
    try {
      const s = localStorage.getItem("calendar_scope");
      return s === "near" || s === "uk" ? s : null;
    } catch { return null; }
  });
  const [radius, setRadiusState] = useState(() => {
    try {
      const r = parseInt(localStorage.getItem("calendar_radius_miles"), 10);
      return r >= 5 && r <= 50 ? r : 15;
    } catch { return 15; }
  });
  // No location set -> gracefully fall back to UK-wide (chip shows a hint).
  const preferred = scope ?? (hasGeo ? "near" : "uk");
  const effectiveScope = preferred === "near" && hasGeo ? "near" : "uk";

  const setScope = (s) => {
    setScopeState(s);
    try { localStorage.setItem("calendar_scope", s); } catch {}
  };
  const setRadius = (r) => {
    setRadiusState(r);
    try { localStorage.setItem("calendar_radius_miles", String(r)); } catch {}
  };

  useEffect(() => { api.calendarEvents(8).then(setOpps).catch(() => {}); }, []);

  // Near me keeps: my collectives' gatherings (inherently local), remote
  // events (labelled), and in-person events within the radius. In-person
  // events WITHOUT coordinates are excluded under Near me (can't be placed);
  // all current data carries coordinates.
  const scoped = useMemo(() => {
    if (effectiveScope !== "near") return opps;
    return opps.filter((o) =>
      o.is_gathering ||
      isRemoteEvent(o) ||
      (o.lat != null && o.lng != null &&
        milesBetween(user.lat, user.lng, o.lat, o.lng) <= radius));
  }, [opps, effectiveScope, radius, user]);

  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const eventDays = useMemo(() => scoped.map(o => new Date(o.occurrence_date || o.event_date)), [scoped]);
  const dayItems = useMemo(() => scoped.filter(o => sameDay(new Date(o.occurrence_date || o.event_date), selected)), [scoped, selected]);

  // Right column filler: the next few events across the whole window, so the
  // panel never sits near-empty next to the calendar (Phase 6 layout balance).
  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return scoped
      .map(o => ({ ...o, _d: new Date(o.occurrence_date || o.event_date) }))
      .filter(o => o._d >= today)
      .sort((a, b) => a._d - b._d)
      .slice(0, 6);
  }, [scoped]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Seo title="Calendar" />
      <BackLink />
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Plan ahead</div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Event calendar</h1>
        </div>
        {user && (
          <button
            data-testid="calendar-subscribe-header-btn"
            onClick={() => setSubscribeOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3.5 py-2 rounded-full border border-forest/30 text-forest hover:bg-forest/5 transition-colors"
          >
            <CalendarPlus className="w-3.5 h-3.5 text-terracotta" />
            Subscribe<span className="hidden sm:inline"> in your calendar app</span>
          </button>
        )}
      </div>
      {user && <CalendarSubscribeDialog open={subscribeOpen} onOpenChange={setSubscribeOpen} />}

      {/* Scope filter (chips match /opportunities filter styling) */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="calendar-scope-filter">
        <button
          data-testid="calendar-scope-uk"
          onClick={() => setScope("uk")}
          className={`text-sm px-4 py-2 rounded-full border transition-colors inline-flex items-center gap-1.5 ${effectiveScope === "uk" ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700 hover:border-forest/40"}`}
        >
          <Globe2 className="w-4 h-4" /> UK-wide
        </button>
        <button
          data-testid="calendar-scope-near"
          onClick={() => hasGeo && setScope("near")}
          disabled={!hasGeo}
          className={`text-sm px-4 py-2 rounded-full border transition-colors inline-flex items-center gap-1.5 ${effectiveScope === "near" ? "bg-forest text-white border-forest" : hasGeo ? "bg-white border-neutral-200 text-neutral-700 hover:border-forest/40" : "bg-white border-neutral-200 text-neutral-400 cursor-not-allowed"}`}
        >
          <LocateFixed className="w-4 h-4" /> Near me
        </button>
        {!hasGeo && (
          <span data-testid="calendar-geo-hint" className="text-xs text-neutral-500">
            <Link to="/onboarding" className="text-forest underline underline-offset-2">Add your location</Link> to see events near you.
          </span>
        )}
        {effectiveScope === "near" && (
          <div className="flex items-center gap-3 ml-1" data-testid="calendar-radius-control">
            <span className="text-xs text-neutral-500 whitespace-nowrap">within <span className="font-semibold text-forest">{radius} miles</span></span>
            <Slider
              data-testid="calendar-radius-slider"
              value={[radius]}
              onValueChange={(v) => setRadius(v[0])}
              min={5} max={50} step={5}
              className="w-36"
            />
            <span className="text-xs text-neutral-400" data-testid="calendar-scope-count">{scoped.length} event{scoped.length === 1 ? "" : "s"}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-8">
        <div className="lg:col-span-3 bg-white border border-neutral-200 rounded-xl p-6">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            modifiers={{ event: eventDays }}
            modifiersClassNames={{ event: "day-has-event" }}
            className="w-full p-0"
            classNames={{
              months: "flex w-full",
              month: "space-y-4 w-full",
              caption_label: "text-base font-medium",
              table: "w-full border-collapse",
              head_row: "flex w-full",
              head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem]",
              row: "flex w-full mt-2",
              cell: "relative flex-1 p-0 text-center text-sm focus-within:relative focus-within:z-20",
              day: "h-12 w-full p-0 font-normal rounded-md hover:bg-forest/10 transition-colors aria-selected:opacity-100",
              day_selected: "!bg-forest !text-white hover:!bg-forest focus:!bg-forest",
              day_today: "font-semibold text-terracotta",
            }}
          />
          <div className="text-xs text-neutral-500 mt-4 flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-terracotta/15 border border-terracotta/40 inline-block" /> Has events
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-forest inline-block" /> Selected day
            </span>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Events on {selected.toLocaleDateString()}</div>
          <div className="mt-3 space-y-3">
            {dayItems.length === 0 && <div className="text-sm text-neutral-500">No events on this day. Try another date.</div>}
            {dayItems.map(o => (
              <Link
                key={o.id}
                to={o.is_gathering ? `/collectives/${o.collective_id}` : `/opportunities/${o.id}`}
                data-testid={o.is_gathering ? `calendar-gathering-${o.id}` : undefined}
                className={`block bg-white border rounded-xl p-5 hover:-translate-y-0.5 transition-transform ${o.is_gathering ? "border-terracotta/40" : "border-neutral-200"}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {o.is_gathering && (
                    <Badge className="rounded-full bg-terracotta/10 text-terracotta border-0 text-[10px] uppercase tracking-wide">Social gathering</Badge>
                  )}
                  {isRemoteEvent(o) && (
                    <Badge data-testid={`calendar-remote-badge-${o.id}`} className="rounded-full bg-forest/10 text-forest border-0 text-[10px] uppercase tracking-wide inline-flex items-center gap-1"><Laptop className="w-3 h-3" />Remote</Badge>
                  )}
                  <div className="font-medium text-forest">{o.title}</div>
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {o.is_gathering ? `${o.collective_name} · hosted by ${o.host_name}` : o.org_name}
                </div>
                <div className="flex flex-wrap gap-3 mt-3 text-sm text-neutral-600">
                  {o.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-forest/60" /> {o.location}</span>}
                  {o.is_gathering ? (
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-forest/60" /> {o.rsvp_count} going</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-forest/60" /> {o.hours_required}h</span>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {upcoming.length > 0 && (
            <div className="mt-8" data-testid="calendar-upcoming">
              <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5 text-terracotta" /> Coming up
              </div>
              <div className="mt-3 space-y-2">
                {upcoming.map(o => (
                  <button
                    key={`${o.id}-${o._d.toISOString()}`}
                    data-testid={`upcoming-event-${o.id}`}
                    onClick={() => setSelected(o._d)}
                    className={`w-full text-left flex items-center gap-3 bg-white border rounded-xl px-4 py-3 hover:border-forest/40 transition-colors ${sameDay(o._d, selected) ? "border-forest/50" : "border-neutral-200"}`}
                  >
                    <span className="shrink-0 w-12 text-center rounded-lg bg-forest/5 border border-forest/10 py-1">
                      <span className="block text-[10px] uppercase tracking-wide text-forest/60 font-bold">{o._d.toLocaleDateString([], { month: "short" })}</span>
                      <span className="block font-display text-lg text-forest leading-tight">{o._d.getDate()}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-forest truncate">{o.title}</span>
                      <span className="block text-xs text-neutral-500 truncate">
                        {o.is_gathering ? `${o.collective_name} · social gathering` : isRemoteEvent(o) ? `${o.org_name} · remote` : o.org_name}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-neutral-400 mt-2">Tap a date to see everything on that day.</p>
            </div>
          )}
        </div>
      </div>

      {user && <CalendarSubscribe />}
    </div>
  );
}
